// frontend/src/components/TransactionHistory.js
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useSnackbar } from "notistack";
import { fetchTransactions } from "../../store/slices/transactionSlice";
import TransactionFilters from "./TransactionFilters";
import TransactionTable from "./TransactionTable";
import TransactionPagination from "./TransactionPagination";
import TransactionDetailsDialog from "./TransactionDetailsDialog";
import { transactionAPI } from "../../services/api";
import { useTranslation } from "react-i18next";

const TransactionHistory = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { transactions, pagination, loading, error } = useSelector(
    (state) => state.transactions
  );
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });
  const { enqueueSnackbar } = useSnackbar();

  const handleFetchTransactions = useCallback(() => {
    dispatch(
      fetchTransactions({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      })
    );
  }, [dispatch, pagination.page, pagination.limit, filters]);

  useEffect(() => {
    handleFetchTransactions();
  }, [handleFetchTransactions]);

  const handleDownloadInvoice = async (transactionId) => {
    try {
      const response = await transactionAPI.downloadInvoice(transactionId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${transactionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar("Failed to download invoice", { variant: "error" });
    }
  };

  if (!loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5">{t("transactions.title")}</Typography>
      <TransactionFilters filters={filters} setFilters={setFilters} />
      <TransactionTable
        transactions={transactions}
        formatAmount={(amount) =>
          new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            signDisplay: "always",
          }).format(amount)
        }
        getStatusColor={(status) =>
          status === "completed"
            ? "success"
            : status === "pending"
            ? "warning"
            : "error"
        }
        getESIMStatusColor={(status) =>
          status === "IN_USE"
            ? "success"
            : status === "SUSPENDED"
            ? "warning"
            : "error"
        }
        setSelectedTransaction={setSelectedTransaction}
        handleDownloadInvoice={handleDownloadInvoice}
      />
      <TransactionPagination
        count={pagination.total}
        page={pagination.page}
        limit={pagination.limit}
        onPageChange={(newPage) =>
          dispatch(fetchTransactions({ ...filters, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          dispatch(fetchTransactions({ ...filters, page: 1, limit: newLimit }))
        }
      />
      <TransactionDetailsDialog
        open={!!selectedTransaction}
        transaction={selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />
    </Box>
  );
};

export default TransactionHistory;
